// The tracked objective's marker: where its `marker` ("kind:id") points in the world, and the map and compass markers
// for it in @rpg/ui's shapes. The kinds are the game's (spike 002: an NPC, a region's centre, a spawn group, a place),
// each resolved by a function it passes.
import { bearingTo, type CompassItem, type MapMarker } from "@rpg/ui";

/** [x, y, z]. */
export type Vec3 = [number, number, number];

/** Where a marker kind's ids are on the ground (x, z), or null when not now (an NPC indoors). */
export type MarkerResolvers = Readonly<Record<string, (id: string) => readonly [number, number] | null>>;

/** A marker ("npc:elder") to a world point, its height from `heightAt`; null when the kind is unknown, the id has no
 *  place now, or the marker is malformed. */
export function resolveMarker(marker: string | null | undefined, resolvers: MarkerResolvers, heightAt: (x: number, z: number) => number): Vec3 | null {
  if (!marker) return null;
  const at = marker.indexOf(":");
  if (at <= 0) return null;
  const kind = marker.slice(0, at);
  const id = marker.slice(at + 1);
  const xz = resolvers[kind]?.(id) ?? null;
  return xz ? [xz[0], heightAt(xz[0], xz[1]), xz[1]] : null;
}

/** The map's (and minimap's) marker for the tracked objective. */
export function objectiveMapMarker(pos: readonly number[]): MapMarker {
  return { id: "objective", x: pos[0]!, z: pos[2]!, icon: "objective" };
}

/** The compass's item for the tracked objective, seen from (x, z). */
export function objectiveCompassItem(x: number, z: number, pos: readonly number[], label = "★"): CompassItem {
  return { id: "objective", bearing: bearingTo(x, z, pos[0]!, pos[2]!), label, tone: "objective" };
}
